export type FrameShape =
  | "square"
  | "circle"
  | "rectangle"
  | "vrect"
  | "hoval"
  | "voval";

export type DesignMode = "nothing" | "text" | "logo";

export type LightStyle = "bar" | "text" | "point";

/**
 * RGB animation style applied to frame-perimeter LEDs when `rgb === true`.
 * Center-text / logo RGB cycles are unaffected — they use their own pattern.
 */
export type AnimationStyle =
  | "classic"
  | "smoothFade"
  | "rapidChange"
  | "chasing"
  | "symmetrical"
  | "pulsing"
  | "converging";

/**
 * CPB lets the customer either pick a preset size (one of the 6 standard
 * inch presets) OR enter width/length manually in inches or centimeters. The
 * viewer keeps both paths in state — `size` drives the preset path, and the
 * `customSize*` fields drive the manual path. Downstream consumers (scene
 * layout) look at `sizeMode` to decide which to use.
 */
export type SizeMode = "preset" | "custom";
export type SizeUnit = "in" | "cm";

/** Default center-text font size (px) for CPB/preview. */
export const DEFAULT_TEXT_FONT_SIZE = 45;
export const TEXT_FONT_SIZE_REF = DEFAULT_TEXT_FONT_SIZE;

/** Logo offset/scale/rotation on the texture (preview behavior matches the live site). */
export interface LogoTransformState {
  offsetPxX: number;
  offsetPxY: number;
  rotationDeg: number;
  /** 100 = default fit; larger values zoom in inside the frame. */
  scalePercent: number;
  flipH: boolean;
}

export const DEFAULT_LOGO_TRANSFORM: LogoTransformState = {
  offsetPxX: 0,
  offsetPxY: 0,
  rotationDeg: 0,
  scalePercent: 100,
  flipH: false,
};

export interface ConfiguratorState {
  shape: FrameShape;
  frameColor: string;
  /** Preset side (square/circle) or long side (rect/oval) in inches. */
  size: number;
  /**
   * Preset short side in inches (rect/oval shapes only). Parsed from the CPB
   * option label (e.g. "M – Classic (24" × 12")" → 12), so the proportion
   * comes from CPB rather than a hardcoded ratio. For square/circle it equals
   * `size`; for rect/oval it falls back to `size / 2` if not provided.
   */
  sizePresetShort: number;
  /** Whether the customer is using a preset or custom size. */
  sizeMode: SizeMode;
  /** Unit used for the custom size counters (driven by CPB radio group). */
  sizeUnit: SizeUnit;
  /** Custom width in the currently-selected unit (in or cm). */
  sizeCustomWidth: number;
  /** Custom length/height in the currently-selected unit (in or cm). */
  sizeCustomLength: number;
  designMode: DesignMode;
  centerText: string;
  /**
   * Outline ("contrast") around the centre text/logo silhouette to lift it off
   * dark or busy backgrounds. CPB picker — "Add contrast?":
   *  - "none"  → no outline (default)
   *  - "light" → white outline around glyphs / logo alpha
   *  - "dark"  → black outline
   * Applies to both `designMode === "text"` (canvas stroke) and
   * `designMode === "logo"` (alpha‑halo composited behind the image).
   */
  centerContrast: "none" | "light" | "dark";
  textColor: string;
  /** Rainbow cycle for center text (texture drawn white; tint via material). */
  textRgb: boolean;
  /** Center / middle design font (Step 1 “Choose Your Font Family”). */
  font: string;
  /** Perimeter “text lighting” font along the frame — separate CPB category. */
  ledTextFont: string;
  /** Font size on the texture (px in canvas coordinates); can be provided by CPB. */
  textFontSize: number;
  /** `blob:` or `data:` URL for TextureLoader; revoke `blob:` URLs on replace/clear/unmount */
  logoImageUrl: string | null;
  /** Revision/hash/timestamp from CPB to force logo refresh when URL stays stable. */
  logoAssetRevision: string;
  /** Rainbow cycle on logo layers (tint via material.color; map unchanged). */
  logoRgb: boolean;
  logoTransform: LogoTransformState;
  ledColor: string;
  lightStyle: LightStyle;
  rgb: boolean;
  /** Frame-LED RGB animation pattern. Ignored when `rgb === false`. */
  animationStyle: AnimationStyle;
  /**
   * Frame-LED RGB color palette index (0..6). Maps to the merchant's CPB
   * picker "Choose Your RGB Color Palette"; row 0 is the default full-rainbow
   * (legacy behavior), 1..6 are merchant-defined color sets. See
   * `palettes.ts` for the keyframe encoding of each row.
   */
  rgbPalette: number;
  depthLayers: number;
  brightness: number;
  /** Text to tile around the frame perimeter in 'text' LED style. */
  ledTextContent: string;
  /** Perimeter text color in Text Lighting mode. */
  ledTextColor: string;
  /** RGB toggle for perimeter text in Text Lighting mode. */
  ledTextRgb: boolean;
  /** Show LED rails above and below the perimeter text. */
  ledRailsEnabled: boolean;
  /** Rails color in Text Lighting mode. */
  ledRailsColor: string;
  /** RGB toggle for rails in Text Lighting mode. */
  ledRailsRgb: boolean;
  /**
   * How many rails per depth band in Text Lighting mode.
   *  - "single" (default): one rail per band, shallow edge for odd `layerNumber`
   *    (line → text) and deep edge for even (mirrored text → line), same
   *    indexing as wall-text bands (`layerNumber = depthLayers - idx`).
   *  - "double": both slab-face rails per band (rail‑text‑rail per layer).
   */
  ledRailsMode: "single" | "double";
}

export const defaultConfiguratorState: ConfiguratorState = {
  shape: "square",
  frameColor: "#111111",
  size: 24,
  sizePresetShort: 12,
  sizeMode: "preset",
  sizeUnit: "in",
  sizeCustomWidth: 24,
  sizeCustomLength: 24,
  designMode: "text",
  centerText: "Enter Text",
  centerContrast: "none",
  textColor: "#ffffff",
  textRgb: false,
  font: "system-ui, sans-serif",
  // Match CPB's text-mode defaults so the optimistic *→text flip in
  // CpbInteractionPauser shows the correct font/color the very first
  // time the user enters text mode (before CPB's own change-event
  // arrives ~500–1100 ms later with the canonical values). On
  // subsequent visits these come from the user's prior selection
  // already persisted in our store, so the optimistic patch is
  // visually identical to CPB's eventual confirmation.
  ledTextFont: "Open Sans",
  textFontSize: DEFAULT_TEXT_FONT_SIZE,
  logoImageUrl: null,
  logoAssetRevision: "",
  logoRgb: false,
  logoTransform: { ...DEFAULT_LOGO_TRANSFORM },
  ledColor: "#ffe8c0",
  lightStyle: "bar",
  rgb: false,
  animationStyle: "classic",
  rgbPalette: 0,
  depthLayers: 11,
  brightness: 1.0,
  ledTextContent: "",
  ledTextColor: "#ffffff",
  ledTextRgb: false,
  ledRailsEnabled: false,
  ledRailsColor: "#ffffff",
  ledRailsRgb: false,
  ledRailsMode: "single",
};
